import type {FillLightMode} from "@/src/modules/core/lib/camera-flash";
import {
  compressImageFile,
  captureVideoFrameToFile,
  type CompressImageOptions,
} from "@/src/modules/core/lib/compress-image";

export type InAppCaptureMethod = "image_capture" | "video_frame";

export type InAppCaptureResult = {
  file: File;
  method: InAppCaptureMethod;
  sourceWidth: number;
  sourceHeight: number;
};

/** Full-res ImageCapture path — downscaled for upload/memory, higher than gallery default. */
export const IN_APP_IMAGE_CAPTURE_COMPRESS: CompressImageOptions = {
  maxDimension: 2560,
  quality: 0.88,
  mimeType: "image/jpeg",
};

/** Preview-frame fallback when ImageCapture is unavailable. */
export const IN_APP_VIDEO_FRAME_CAPTURE: CompressImageOptions = {
  maxDimension: 1920,
  quality: 0.85,
  mimeType: "image/jpeg",
};

/** Minimal ImageCapture typing — not in all TS DOM lib versions. */
type ImageCaptureInstance = {
  takePhoto: (photoSettings?: {
    imageWidth?: number;
    imageHeight?: number;
    fillLightMode?: FillLightMode;
  }) => Promise<Blob>;
};

type ImageCaptureConstructor = new (track: MediaStreamTrack) => ImageCaptureInstance;

function getImageCaptureConstructor(): ImageCaptureConstructor | null {
  const ctor = (globalThis as {ImageCapture?: ImageCaptureConstructor}).ImageCapture;
  return ctor ?? null;
}

function isImageCaptureSupported(): boolean {
  return getImageCaptureConstructor() !== null;
}

function getLiveVideoTrack(stream: MediaStream): MediaStreamTrack | null {
  const track = stream.getVideoTracks()[0];
  if (!track || track.readyState !== "live") {
    return null;
  }
  return track;
}

async function readImageDimensions(
  file: File,
): Promise<{width: number; height: number} | null> {
  if (typeof createImageBitmap !== "function") {
    return null;
  }

  try {
    const bitmap = await createImageBitmap(file, {imageOrientation: "from-image"});
    const dimensions = {width: bitmap.width, height: bitmap.height};
    bitmap.close();
    return dimensions;
  } catch {
    return null;
  }
}

async function captureWithImageCapture(
  track: MediaStreamTrack,
  fileName: string,
  fillLightMode?: FillLightMode,
): Promise<File | null> {
  try {
    const ImageCapture = getImageCaptureConstructor();
    if (!ImageCapture) {
      return null;
    }
    const imageCapture = new ImageCapture(track);
    const photoSettings = fillLightMode ? {fillLightMode} : undefined;
    const blob = await imageCapture.takePhoto(photoSettings);
    if (!blob || blob.size === 0) {
      return null;
    }

    return new File([blob], fileName, {
      type: blob.type || "image/jpeg",
      lastModified: Date.now(),
    });
  } catch {
    return null;
  }
}

/**
 * Capture a still photo from an in-page getUserMedia session.
 * Prefers ImageCapture.takePhoto() (sensor resolution, WebView stays foreground).
 * Falls back to a canvas grab from the preview video element.
 */
export async function captureInAppPhoto(
  stream: MediaStream,
  video: HTMLVideoElement,
  options: {fileName?: string; fillLightMode?: FillLightMode} = {},
): Promise<InAppCaptureResult | null> {
  const fileName = options.fileName ?? `photo-${Date.now()}.jpg`;
  const track = getLiveVideoTrack(stream);

  if (track && isImageCaptureSupported()) {
    const rawPhoto = await captureWithImageCapture(track, fileName, options.fillLightMode);
    if (rawPhoto) {
      const dimensions =
        (await readImageDimensions(rawPhoto)) ?? {
          width: video.videoWidth,
          height: video.videoHeight,
        };

      const file = await compressImageFile(rawPhoto, IN_APP_IMAGE_CAPTURE_COMPRESS);

      return {
        file,
        method: "image_capture",
        sourceWidth: dimensions.width,
        sourceHeight: dimensions.height,
      };
    }
  }

  const file = await captureVideoFrameToFile(video, {
    ...IN_APP_VIDEO_FRAME_CAPTURE,
    fileName,
  });
  if (!file) {
    return null;
  }

  return {
    file,
    method: "video_frame",
    sourceWidth: video.videoWidth,
    sourceHeight: video.videoHeight,
  };
}
